import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { admins } from "../../Constants";
import { getSiteBanner, setSiteBanner, SiteBanner } from "../../services/SiteBannerService";
import styles from "./SiteBannerAdminPage.module.css";

const SiteBannerAdminPage: React.FC = () => {
    const { user, token } = useContext(AuthContext);
    const [message, setMessage] = useState("");
    const [isActive, setIsActive] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState<{ type: "ok" | "err"; text: string } | null>(null);
    const [existing, setExisting] = useState<SiteBanner | null>(null);

    const isAdmin = user && admins.includes(user.steamID);

    useEffect(() => {
        (async () => {
            try {
                const banner = await getSiteBanner();
                setExisting(banner);
                setMessage(banner.message || "");
                setIsActive(banner.isActive);
            } catch {
                setStatus({ type: "err", text: "Failed to load current banner." });
            }
            setLoading(false);
        })();
    }, []);

    const handleSave = async (activeOverride?: boolean) => {
        if (!token) return;
        setSaving(true);
        setStatus(null);
        const nextActive = activeOverride !== undefined ? activeOverride : isActive;
        try {
            const banner = await setSiteBanner(message, nextActive, token);
            setExisting(banner);
            setIsActive(banner.isActive);
            setStatus({
                type: "ok",
                text: nextActive ? "Banner is live on the homepage." : "Banner cleared.",
            });
        } catch (err: any) {
            setStatus({
                type: "err",
                text: err?.response?.data || "Failed to save banner.",
            });
        }
        setSaving(false);
    };

    const handleClear = async () => {
        setIsActive(false);
        await handleSave(false);
    };

    if (!isAdmin) {
        return (
            <div className={styles.denied}>
                <h2>Access Denied</h2>
                <p>Admin only.</p>
            </div>
        );
    }

    if (loading) {
        return <div className={styles.container}><p>Loading…</p></div>;
    }

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Site Banner</h1>
            <p className={styles.subtitle}>
                A message shown at the top of the homepage. Useful for announcing outages, maintenance, or
                short notices without pushing a new build.
            </p>

            {status && (
                <div className={status.type === "ok" ? styles.success : styles.error}>{status.text}</div>
            )}

            <div className={styles.formCard}>
                <div className={styles.fieldLabel}>Message</div>
                <textarea
                    className={styles.textarea}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="e.g. Demo parsing is currently broken — we're working on it."
                    rows={4}
                />

                <label className={styles.checkbox}>
                    <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <span>Show banner on homepage</span>
                </label>

                <div className={styles.formActions}>
                    <button
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        onClick={() => handleSave()}
                        disabled={saving}
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                        className={`${styles.btn} ${styles.btnSecondary}`}
                        onClick={handleClear}
                        disabled={saving || (!existing?.isActive && !isActive)}
                    >
                        Clear & hide
                    </button>
                </div>

                {existing?.updatedAt && (
                    <div className={styles.meta}>
                        Last updated: {new Date(existing.updatedAt).toLocaleString()}
                        {existing.updatedBySteamId && ` — by ${existing.updatedBySteamId}`}
                    </div>
                )}
            </div>

            {message && (
                <div className={styles.preview}>
                    <div className={styles.previewLabel}>Preview</div>
                    <div className={styles.previewBanner}>{message}</div>
                </div>
            )}
        </div>
    );
};

export default SiteBannerAdminPage;
